/**
 * Email sold by Connect, as the API describes it to staff
 * (`app.modules.emailing.admin_router`): one price for the platform and a
 * minimum under it, the three levels at which sending is stopped — all email,
 * one client, one domain — and each client's prepaid wallet.
 */

export interface EmailPricing {
  currency: string;
  /** What a client is charged per email unless it has a price of its own. */
  price: string;
  /** No client is charged less, whatever its own price says. */
  minimum_price: string;
  /** One incoming email accepted for processing; null is no price — nothing is processed. */
  inbound_price: string | null;
  /** Emails one incoming address may take in an hour. */
  inbound_hourly_cap: number;
  /** Days a message the wallet cannot pay for is kept. */
  inbound_hold_days: number;
  /** The size an incoming email's price covers, in MB. */
  inbound_included_mb: number;
  /** What each started MB past that adds; "0" adds nothing. */
  inbound_price_per_extra_mb: string;
  /** The most an email written in a client's inbox may carry in files, in MB. */
  attachment_max_mb: number;
  /** What each started MB of an email's files adds, for each recipient; "0" adds nothing. */
  attachment_price_per_mb: string;
}

/**
 * A client domain's own link address (`links.<domain>`): where its marketing
 * email's unsubscribe link points once ready. `connecting` means the record is
 * in place and the machine's edge has no certificate for it yet — an operator
 * runs `scripts/place-link-hosts.sh` (docs/20_EMAIL.md §15).
 */
export interface EmailLinkAddress {
  host: string;
  state: 'pending' | 'connecting' | 'ready';
  checked_at: string | null;
  ready_at: string | null;
}

/** Clients' link addresses across the installation, for whoever runs it. */
export interface EmailLinkHosts {
  /** What each client's `links` CNAME points at. */
  target: string;
  ready: number;
  waiting: { host: string; client: string; client_name: string; checked_at: string | null }[];
}

export interface EmailOverview {
  pricing: EmailPricing;
  sending: {
    paused: boolean;
    note: string;
    /** Stopped by the installation's environment: not this screen's to lift. */
    paused_by_environment: boolean;
  };
  /** Which mail backend the installation is configured with. */
  backend: string;
  reports_configured: boolean;
  /** Whether opens and clicks can be recorded at all (a tracking set is configured). */
  tracking_available: boolean;
  limits: EmailLimits;
  /** The client API features' own lines (`email.client_api`). */
  client_api: EmailClientApi;
  budget: EmailBudget;
  /** The account's standing at the mail service; null where there is no service to ask. */
  account: EmailAccountState | null;
  /** Null where the installation offers no link addresses (`EMAIL_LINK_TARGET` unset). */
  links: EmailLinkHosts | null;
}

export interface WarmupStage {
  daily: number;
  hourly: number;
}

/** The warm-up ladder and the lines Connect's watch acts at (`email.limits`). Rates are percentages. */
export interface EmailLimits {
  new: WarmupStage;
  building: WarmupStage;
  established: WarmupStage;
  to_building: { days: number; sent: number };
  to_established: { days: number; sent: number };
  throttle_bounce: number;
  throttle_complaint: number;
  pause_bounce: number;
  pause_complaint: number;
  minimum_volume: number;
  spike_factor: number;
}

/**
 * A client's own email content — its rate limits and the lines at which it stops by itself —
 * and how many addresses one email with Cc and Bcc may name (`email.client_api`). Rates are
 * percentages.
 */
export interface EmailClientApi {
  direct_per_hour: number;
  direct_per_day: number;
  direct_pause_bounce: number;
  direct_pause_complaint: number;
  direct_minimum_volume: number;
  max_copy_addresses: number;
}

/** The month's spend at the mail service, worked out by Connect, and its ceiling. */
export interface EmailBudget {
  aws_rate_per_1000: string;
  monthly_budget_usd: string;
  month: string;
  client_messages: number;
  own_messages: number;
  projects: number;
  cost_usd: string;
  budget_usd: string;
  /** Share of the budget spent, 0 to 1 (and past 1 when over). */
  used: number;
}

export type EmailAccountState =
  | { error: string }
  | {
      production: boolean;
      sending_enabled: boolean;
      enforcement: string;
      max_24_hours: number;
      max_per_second: number;
      sent_24_hours: number;
      recorded_24_hours: number;
      /** The service counted more than Connect recorded: mail Connect does not know it sent. */
      unrecorded: boolean;
    };

export interface EmailStats {
  accepted: number;
  delivered: number;
  bounced: number;
  soft_bounced: number;
  complained: number;
  refused: number;
  opened: number;
  clicked: number;
  bounce_rate: number | null;
  complaint_rate: number | null;
  delivery_rate: number | null;
  open_rate: number | null;
  click_rate: number | null;
}

export interface EmailWalletEntry {
  public_id: string;
  kind: 'top_up' | 'refund' | 'adjustment' | 'hold' | 'release' | string;
  amount: string;
  balance_after: string;
  description: string;
  /** On a hold or a release: how many emails, at what price. */
  usage: { emails?: number; rate?: string };
  at: string;
}

export interface EmailWallet {
  public_id: string;
  balance: string;
  currency: string;
  /** What one email costs this client now: its own price or the platform's, never under the minimum. */
  price: string;
  minimum_top_up: string;
  low_balance_threshold: string | null;
  low: boolean;
  spent_30_days: string;
  entries: EmailWalletEntry[];
  has_more: boolean;
}

export interface EmailDomain {
  public_id: string;
  domain: string;
  status: 'pending' | 'verified' | 'failed' | string;
  project: string;
  /** Connect's name for the project at the mail service: what an operator looks it up by. */
  project_reference: string;
  accounts: number;
  paused: boolean;
  paused_at: string | null;
  paused_note: string | null;
  verified_at: string | null;
  warmup_stage: 'new' | 'building' | 'established' | 'custom' | string;
  warmup_since: string | null;
  /** Its caps now — halved while it is throttled. */
  daily_cap: number;
  hourly_cap: number;
  sent_last_day: number;
  sent_last_hour: number;
  throttled: boolean;
  throttle_reason: 'rates' | 'spike' | 'service' | string | null;
  /** The last seven days, counted. */
  week: EmailStats;
  /** Its own link address; null where the installation offers none. */
  links: EmailLinkAddress | null;
}

/** A client's incoming email, as staff see it (`email-inbound.tsx`). */
export interface InboundPanel {
  /** Whether this installation offers incoming email at all. */
  offered: boolean;
  allowed: boolean;
  note: string | null;
  own_price: string | null;
  platform_price: string | null;
  /** What it is charged: its own price, else the platform's — null is none. */
  price: string | null;
  enabled: boolean;
  when_short: 'hold' | 'reject';
  tickets: boolean;
  addresses: number;
  counts_30_days: Record<string, number>;
}

export interface EmailClient {
  public_id: string;
  name: string;
  has_service: boolean;
  /** Opens and clicks of its marketing mail are recorded. */
  tracking: boolean;
  tracking_available: boolean;
  /** Its own price, or null for the platform's. */
  own_price: string | null;
  platform_price: string;
  minimum_price: string;
  sending_paused: boolean;
  sending_paused_at: string | null;
  sending_paused_note: string | null;
  domains: EmailDomain[];
  wallet: EmailWallet;
  inbound: InboundPanel;
}

export interface BouncedAddress {
  public_id: string;
  address: string;
  created_at: string;
}

export interface EmailSuppression {
  public_id: string;
  email: string;
  reason: 'manual' | 'stop' | 'bounce' | 'complaint' | 'unsubscribe' | string;
  note: string | null;
  added_by: 'client' | 'staff' | 'delivery' | 'recipient' | string;
  created_at: string;
}

export interface EmailSuppressionAdded {
  added: number;
  already_suppressed: number;
  invalid: string[];
}

// ── the template review (internal: never shown to a client) ──
export interface ReviewRule {
  key: string;
  title: string;
  /** What the reviewer checks. */
  check: string;
  severity: 'block' | 'warn';
  /** What the client reads when its template fails this rule. */
  client_message: string;
  applies_to: 'all' | 'marketing' | 'transactional';
  enabled: boolean;
}

export interface ReviewPolicy {
  enabled: boolean;
  staff_check: 'after' | 'before';
  max_risk: number;
  model: string | null;
  temperature: number;
  max_output_tokens: number;
  /** How much the model thinks before judging (the AI gateway's `reasoning`); null: the model's default. */
  reasoning: 'off' | 'low' | 'medium' | 'high' | null;
  max_attempts: number;
  instructions: string[];
  rules: ReviewRule[];
  /** The rules a fresh installation starts with, for "reset". Read only. */
  defaults?: ReviewRule[];
}

export interface ReviewRow {
  public_id: string;
  client_public_id: string;
  client_name: string;
  template_name: string;
  category: string | null;
  status: 'pending' | 'approved' | 'rejected' | string;
  ai_status: 'pending' | 'running' | 'done' | 'failed' | 'skipped' | string;
  ai_decision: 'approve' | 'reject' | null;
  ai_risk: number | null;
  staff_decision: 'approved' | 'rejected' | null;
  created_at: string;
}

export interface ReviewFinding {
  rule: string;
  title: string;
  severity: 'block' | 'warn';
  result: 'pass' | 'fail' | 'unclear';
  evidence: string;
  recommendation: string;
}

export interface ReviewLink {
  href: string;
  text: string;
  host: string;
  own_domain: boolean;
  shortener: boolean;
  bare_ip: boolean;
  download: boolean;
  link_text_mismatch: boolean;
}

export interface ReviewDetail {
  public_id: string;
  /** This is the template's newest version: a decision may be made on it. */
  current: boolean;
  client: { public_id: string; name: string };
  template: {
    public_id: string;
    name: string;
    category: string | null;
    subject: string;
    html: string;
    text: string;
    deleted: boolean;
  };
  status: 'pending' | 'approved' | 'rejected' | string;
  ai: {
    status: string;
    attempts: number;
    decision: 'approve' | 'reject' | null;
    risk: number | null;
    summary: string;
    findings: ReviewFinding[];
    recommendations: string[];
    model: string | null;
    tokens: number;
    error: string | null;
    reviewed_at: string | null;
  };
  facts: {
    links?: ReviewLink[];
    images?: number;
    html_bytes?: number;
    text_length?: number;
    subject_capitals_share?: number;
    subject_exclamations?: number;
    unsubscribe_placed?: boolean;
    sender_domains?: string[];
  };
  client_reasons: string[];
  client_sees: { sending_status: string; sending_notes: string[] };
  staff: { decision: 'approved' | 'rejected' | null; note: string | null; at: string | null; by: string | null };
  rules: { key: string; title: string; severity: 'block' | 'warn' }[];
  history: {
    public_id: string;
    status: string;
    ai_decision: string | null;
    staff_decision: string | null;
    created_at: string;
  }[];
  created_at: string;
}

export const EMAIL_ENDPOINT = '/messaging/email';

export const emailKeys = {
  overview: ['email', 'overview'] as const,
  bounced: ['email', 'bounced'] as const,
  client: (id: string) => ['email', 'client', id] as const,
  suppressions: (id: string) => ['email', 'client', id, 'suppressions'] as const,
  reviews: ['email', 'reviews'] as const,
  review: (id: string) => ['email', 'reviews', id] as const,
  reviewRules: ['email', 'review-rules'] as const,
};

/** The body of every stop and restart, at all three levels. */
export interface PauseBody {
  paused: boolean;
  note: string;
}
